/**
 * AuthedAvatarImage — fetches a Bearer-token-protected image URL via
 * `fetch()` and renders it through `URL.createObjectURL(blob)` so it
 * works in places where a plain `<img src="…">` would fail because
 * the browser can't send `Authorization: Bearer …` headers.
 *
 * Use this anywhere an avatar URL (`/api/users/{id}/avatar`) must be
 * rendered: the AvatarUploadCard preview, the header pill, chat
 * bubbles, etc.
 *
 * Props
 *   token      — auth token (required)
 *   src        — full URL or relative path (will be prefixed with
 *                REACT_APP_BACKEND_URL when relative)
 *   alt        — img alt text
 *   className  — passed straight to <img>
 *   fallback   — React node rendered while loading OR if the fetch
 *                fails. Pass initials here so we always have a graceful
 *                fallback.
 *   testid
 */
import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module-scoped tiny cache so multiple avatars for the same user don't
// hit the network repeatedly (e.g. when the user appears in many chat
// messages). Cache is keyed by full URL (which already carries the
// `?v={hash}` version stamp the backend sets).
const _cache = new Map();   // url -> objectUrl

function _abs(src) {
  if (!src) return null;
  if (/^https?:\/\//.test(src)) return src;
  return `${API_URL}${src.startsWith('/') ? '' : '/'}${src}`;
}

export default function AuthedAvatarImage({ token, src, alt, className, fallback = null, testid }) {
  const [objUrl, setObjUrl] = useState(null);
  const [errored, setErrored] = useState(false);
  const abs = _abs(src);

  useEffect(() => {
    if (!abs || !token) { setObjUrl(null); setErrored(false); return; }
    // Cache hit — synchronous render with no flicker.
    if (_cache.has(abs)) {
      setObjUrl(_cache.get(abs));
      setErrored(false);
      return;
    }
    let cancelled = false;
    let createdUrl = null;
    setErrored(false);
    (async () => {
      try {
        const r = await fetch(abs, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        createdUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          _cache.set(abs, createdUrl);
          setObjUrl(createdUrl);
        } else {
          URL.revokeObjectURL(createdUrl);
        }
      } catch (_e) {
        if (!cancelled) setErrored(true);
      }
    })();
    return () => {
      cancelled = true;
      // We deliberately do NOT revoke object URLs here — they live in
      // the module cache for the lifetime of the SPA so re-mounting the
      // same avatar (e.g. switching between Profile and Dashboard) is
      // instant. The cache is small (one entry per avatar version) and
      // bounded by user activity.
    };
  }, [abs, token]);

  if (errored || !objUrl) {
    return fallback;
  }
  return <img src={objUrl} alt={alt} className={className} data-testid={testid} />;
}

// Optional helper: drop a cached object URL when an avatar is replaced
// or removed so the next render forces a fresh fetch.
export function bustAuthedAvatarCache(src) {
  const abs = _abs(src);
  if (!abs) return;
  const prev = _cache.get(abs);
  if (prev) {
    try { URL.revokeObjectURL(prev); } catch (_) { /* noop */ }
    _cache.delete(abs);
  }
}
